<template>
  <div class="calculator-panel">
    <div class="calculator-header">
      <h3 class="calculator-title">{{ $t('legacy.Калькулятор') }}</h3>
      <button class="calculator-close" @click="$emit('close')">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    </div>
    
    <div class="calculator-summary">
      <div class="summary-row">
        <span>{{ $t('legacy.Выбрано объектов:') }}</span>
        <span class="summary-value">{{ checkedCount }} / {{ totalCount }}</span>
      </div>
      <div v-if="canSeePrices" class="summary-row total">
        <span>{{ $t('legacy.Итого:') }}</span>
        <div class="total-right">
          <span class="summary-value">{{ calculatedTotal }} ₽</span>
          <button
            class="refresh-prices-btn"
            :class="{ spinning: refreshing }"
            @click="triggerRefresh"
            :title="$t('legacy.Обновить цены из каталога')"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
          </button>
        </div>
      </div>
      <div v-else class="summary-row upgrade-hint">
        <span>{{ $t('legacy.Цены доступны на платном тарифе') }}</span>
      </div>
    </div>
    
    <div class="calculator-list">
      <div v-if="groupedItems.length === 0" class="calculator-empty">
        {{ $t('legacy.Нет элементов для расчёта') }}
      </div>
      
      <div v-for="group in groupedItems" :key="group.name" class="calculator-group">
        <div class="group-header">
          <span class="group-name">{{ group.name }}</span>
          <span class="group-count">({{ group.items.length }})</span>
        </div>
        
        <div 
          v-for="item in group.items" 
          :key="item.id"
          class="calculator-item"
          :class="{ 'is-highlighted': highlightedItemId === item.id }"
        >
          <label class="item-checkbox">
            <input 
              type="checkbox" 
              :checked="!excludedItems.includes(item.id)"
              @change="toggleItem(item.id)"
            />
            <span class="checkmark"></span>
          </label>
          
          <div class="item-preview" @click="highlightItem(item)">
            <img v-if="item.src && !item.isText" :src="item.src" alt="" class="item-thumb" />
            <div v-else-if="item.isText" class="item-text-icon">T</div>
            <div v-else class="item-placeholder"></div>
          </div>
          
          <div class="item-info" @click="highlightItem(item)">
            <span class="item-name">{{ item.name || $t('editor.element') }}</span>
          </div>
          
          <span v-if="canSeePrices" class="item-price">{{ item.effectivePrice || 0 }} ₽</span>
          <span v-else class="item-price locked">—</span>
          
          <button class="item-delete" @click="deleteItem(item.id)" :title="$t('common.delete')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M19 7L18.1327 19.1425C18.0579 20.1891 17.187 21 16.1378 21H7.86224C6.81296 21 5.94208 20.1891 5.86732 19.1425L5 7M10 11V17M14 11V17M15 7V4C15 3.44772 14.5523 3 14 3H10C9.44772 3 9 3.44772 9 4V7M4 7H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CalculatorPanel',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    canSeePrices: {
      type: Boolean,
      default: true
    }
  },
  emits: ['close', 'highlight-item', 'delete-item', 'update-excluded', 'refresh-prices'],
  data() {
    return {
      excludedItems: [],
      highlightedItemId: null,
      highlightTimeout: null,
      refreshing: false,
      refreshTimeout: null
    };
  },
  computed: {
    filteredItems() {
      return this.items.filter(item => 
        item && 
        !item.isColor && 
        !item.isBackground && 
        !item._isBackground &&
        (item.effectivePrice > 0 || item.name)
      );
    },
    groupedItems() {
      const groups = {};
      
      this.filteredItems.forEach(item => {
        const groupName = item.rootCategoryName || this.$t('Без категории');
        if (!groups[groupName]) {
          groups[groupName] = {
            name: groupName,
            items: []
          };
        }
        groups[groupName].items.push(item);
      });
      
      return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
    },
    totalCount() {
      return this.filteredItems.length;
    },
    checkedCount() {
      return this.filteredItems.filter(item => !this.excludedItems.includes(item.id)).length;
    },
    calculatedTotal() {
      return this.filteredItems
        .filter(item => !this.excludedItems.includes(item.id))
        .reduce((sum, item) => sum + (item.effectivePrice || 0), 0);
    }
  },
  methods: {
    toggleItem(itemId) {
      const index = this.excludedItems.indexOf(itemId);
      if (index > -1) {
        this.excludedItems.splice(index, 1);
      } else {
        this.excludedItems.push(itemId);
      }
      this.$emit('update-excluded', { excludedItems: [...this.excludedItems], calculatedTotal: this.calculatedTotal });
    },
    highlightItem(item) {
      if (this.highlightTimeout) {
        clearTimeout(this.highlightTimeout);
      }
      this.highlightedItemId = item.id;
      this.$emit('highlight-item', item.id);
      
      this.highlightTimeout = setTimeout(() => {
        this.highlightedItemId = null;
        this.$emit('highlight-item', null);
      }, 2000);
    },
    deleteItem(itemId) {
      this.$emit('delete-item', itemId);
    },
    triggerRefresh() {
      if (this.refreshing) return;
      this.refreshing = true;
      this.$emit('refresh-prices');
      if (this.refreshTimeout) clearTimeout(this.refreshTimeout);
      this.refreshTimeout = setTimeout(() => {
        this.refreshing = false;
      }, 2000);
    }
  },
  beforeUnmount() {
    if (this.highlightTimeout) {
      clearTimeout(this.highlightTimeout);
    }
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
  }
};
</script>

<style lang="scss" scoped>
.calculator-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
}

.calculator-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.calculator-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.calculator-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: #666;
  transition: background 0.2s, color 0.2s;
  
  &:hover {
    background: #f0f0f0;
    color: #333;
  }
}

.calculator-summary {
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #666;
  
  &.total {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e0e0e0;
    font-size: 15px;
    font-weight: 600;
    color: #333;
  }
  
  &.upgrade-hint {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e0e0e0;
    font-size: 12px;
    color: #999;
    font-style: italic;
  }
}

.summary-value {
  font-weight: 500;
}

.total-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.refresh-prices-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #496782;
  transition: background 0.2s, color 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #e0f4fc;
    color: #26374E;
  }

  &.spinning svg {
    animation: spin 0.8s linear infinite;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.calculator-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
}

.calculator-empty {
  padding: 32px 16px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.calculator-group {
  margin-bottom: 8px;
}

.group-header {
  padding: 8px 16px;
  background: #f5f5f5;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.group-count {
  font-weight: normal;
  color: #999;
}

.calculator-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;
  
  &:hover {
    background: #fafafa;
  }
  
  &.is-highlighted {
    background: #e3f2fd;
    animation: highlight-pulse 0.5s ease-out;
  }
}

@keyframes highlight-pulse {
  0% { background: #bbdefb; }
  100% { background: #e3f2fd; }
}

.item-checkbox {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  cursor: pointer;
  
  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .checkmark {
    position: relative;
    width: 18px;
    height: 18px;
    border: 2px solid #ccc;
    border-radius: 4px;
    transition: all 0.2s;
    
    &::after {
      content: '';
      display: none;
      position: absolute;
      left: 50%;
      top: 45%;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: translate(-50%, -50%) rotate(45deg);
    }
  }
  
  input:checked + .checkmark {
    background: #BBCAD8;
    border-color: #BBCAD8;
    
    &::after {
      display: block;
    }
  }
}

.item-preview {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  background: #f0f0f0;
  cursor: pointer;
}

.item-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-text-icon {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  color: #667eea;
  background: #e8eaf6;
}

.item-placeholder {
  width: 100%;
  height: 100%;
  background: #e0e0e0;
}

.item-info {
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.item-name {
  display: block;
  font-size: 13px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-price {
  font-size: 13px;
  font-weight: 500;
  color: #26374E;
  white-space: nowrap;
  
  &.locked {
    color: #ccc;
  }
}

.item-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #999;
  transition: all 0.2s;
  
  &:hover {
    background: #ffebee;
    color: #e53935;
  }
}
</style>
